<template>
  <NuxtLink
    v-if="hasContent && link && !disabled && link !== '#start-project#'"
    :to="resolvedLink"
    :target="`_${target}`"
    class="w-fit border-solid border border-transparent duration-300 shine-button relative overflow-hidden flex justify-center items-center font-saira uppercase"
    :class="buttonContainerClass"
    :style="buttonStyle"
  >
    <span v-if="label" class="font-medium" :class="fontSizeClasses">{{
      label
    }}</span>
    <i v-if="icon" :class="iconClass"></i>
  </NuxtLink>

  <button
    v-else-if="hasContent"
    type="button"
    :aria-label="ariaLabel"
    class="w-fit border-solid border border-transparent duration-300 shine-button relative overflow-hidden flex justify-center items-center font-semibold cursor-pointer font-saira uppercase"
    :class="buttonBaseClass"
    :style="buttonStyle"
    :disabled="disabled"
    @click="link === '#start-project#' ? handleModal() : ''"
  >
    <span v-if="label" class="font-medium" :class="fontSizeClasses">{{
      label
    }}</span>
    <i v-if="icon" :class="iconClass"></i>
  </button>
</template>
<script setup>
import { computed } from "vue";
import { useLocalePath } from "#i18n";
import { useModalStore, useSiteSettingsStore } from "@/store/index";
const useModal = useModalStore();
const siteSettingsStore = useSiteSettingsStore();

const props = defineProps({
  label: { type: String },
  link: { type: String },
  color: { type: String, default: "first" },
  icon: { type: String },
  iconPosition: { type: String, default: "left" },
  size: { type: String, default: "small" },
  fontSize: { type: String, default: "base" },
  target: { type: String, default: "self" },
  disabled: { type: Boolean, default: false },
});

const localePath = useLocalePath();

const hasContent = computed(() => !!(props.label || props.icon));
const ariaLabel = computed(() => props.label || undefined);

const resolvedLink = computed(() => {
  if (!props.link) {
    return null;
  }
  if (typeof props.link === "string") {
    // External / non-navigational links pass through untouched.
    if (/^(https?:|mailto:|tel:|\/\/|#)/i.test(props.link)) {
      return props.link;
    }
    // Internal paths (absolute "/..." or relative) get the locale prefix so
    // e.g. "/2d" resolves to "/en/2d" on the English site instead of the tr one.
    return localePath(props.link);
  }
  if (typeof props.link === "object" && "path" in props.link) {
    return props.link.path;
  }
  return localePath(props.link);
});

const sizeClasses = computed(() => {
  return props.size === "big"
    ? "px-12 py-3.5"
    : props.size === "medium"
    ? "px-16 py-3"
    : "px-5 py-3";
});

const fontSizeClasses = computed(() => {
  return props.fontSize === "xs"
    ? "text-xs"
    : props.fontSize === "sm"
    ? "text-sm"
    : props.fontSize === "lg"
    ? "text-lg"
    : props.fontSize === "xl"
    ? "text-xl"
    : props.fontSize === "2xl"
    ? "text-2xl"
    : "text-base";
});

const buttonStyle = computed(() => {
  if (props.color?.startsWith("#")) {
    return {
      backgroundColor: props.color,
      borderColor: props.color,
      color: "#fff",
    };
  }
  return {};
});

const buttonClasses = computed(() => {
  if (props.color?.startsWith("#")) return "";

  const colorMap = {
    first:
      "bg-brand-first text-white hover:border-brand-first lg:hover:bg-white lg:hover:text-brand-first",
    second:
      "bg-brand-second text-white hover:border-brand-second lg:hover:bg-white lg:hover:text-brand-second",
    white: "bg-white text-black lg:hover:bg-[#bdbdbd]",
    black: "bg-black text-white lg:hover:bg-[#323232]",
    "black-outline":
      "bg-transparent text-black !border-black lg:hover:bg-black lg:hover:text-white",
    "white-outline":
      "bg-transparent text-white !border-white lg:hover:bg-white lg:hover:text-black",
    "first-outline":
      "bg-transparent text-brand-first !border-brand-first lg:hover:bg-brand-first lg:hover:text-white",
    "second-outline":
      "bg-transparent text-brand-second !border-brand-second lg:hover:bg-brand-second lg:hover:text-white",
  };

  return colorMap[props.color] || colorMap.first;
});

const iconNeedsBase = computed(() => {
  if (!props.icon || typeof props.icon !== "string") return false;

  const classes = props.icon.split(" ");
  const hasPi = classes.includes("pi");
  const hasPiDash = classes.some((cls) => cls.startsWith("pi-"));

  return hasPiDash && !hasPi;
});

const handleModal = () => {
  useModal.triggerModal();
};
const { isMobile } = useIsMobile();

const buttonContainerClass = computed(() => [
  sizeClasses.value,
  { 'rounded-lg': siteSettingsStore.roundCorner },
  buttonClasses.value,
]);
const buttonBaseClass = computed(() => [
  sizeClasses.value,
  { 'rounded-lg': siteSettingsStore.roundCorner },
  buttonClasses.value,
  { 'opacity-40 !cursor-not-allowed': props.disabled },
]);
const iconClass = computed(() => [
  iconNeedsBase.value ? 'pi' : null,
  props.icon,
  props.label
    ? props.iconPosition === 'left'
      ? 'mr-1.5 -order-1'
      : 'ml-1.5 order-1'
    : null,
]);
</script>
